import React from 'react';
import { navigate } from 'gatsby-link';
import { useTranslation } from 'react-i18next';
import { createSelector } from 'reselect';
import { Dispatch, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Modal } from '@devstart/react-bootstrap';
import { TFunction } from 'i18next';
import Button from '../../ui/Button';
import { ButtonTypes } from '../../ui/Button/button-types';
import Close3 from '../../../assets/icons/close3';
import medal from '../../../assets/images/medal.png';
import { userSelector } from '../../../redux/selectors';
import {
  challengeMetaSelector,
  isNacionalCertificateOpenSelector
} from '../../../templates/Challenges/redux/selectors';
import { ChallengeMeta, User } from '../../../redux/prop-types';
import { closeModal } from '../../../templates/Challenges/redux/actions';
import { AccessLevel } from '../../../utils/enums/access-levels';
import {
  getCheckpointById,
  getCheckpointIndexById
} from '../../../../../utils/nacional-utils';

import './modal-cta-registration.css';

type ModalCtaRegistrationProps = {
  readonly isOpen: boolean;
  readonly user: User;
  readonly challengeMeta: ChallengeMeta;
  readonly close: () => void;
};

const mapStateToProps = createSelector(
  userSelector,
  isNacionalCertificateOpenSelector,
  challengeMetaSelector,
  (user: User, isOpen: boolean, challengeMeta: ChallengeMeta) => ({
    user,
    isOpen,
    challengeMeta
  })
);

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      close: () => closeModal('nacionalCertificate')
    },
    dispatch
  );

const certificates = [
  'fundamentals-of-front-end-programming',
  'front-end-developer-react',
  'react-advanced-navigation-and-interactivity-web-development',
  'react-with-web-routes-and-interactions'
];

interface ModalStepsProps {
  t: TFunction;
  concludedIndex: number;
  registrationQuiz: number;
}

const ModalSteps: React.FC<ModalStepsProps> = ({
  t,
  concludedIndex,
  registrationQuiz
}) => {
  const filteredCertificates =
    registrationQuiz === 2 ? certificates.slice(1) : certificates;

  return (
    <ul className='steps'>
      {filteredCertificates.map((certificate, index) => (
        <li key={certificate}>
          <div
            className='bullet'
            style={{
              background: index <= concludedIndex ? 'green' : 'white',
              color: index <= concludedIndex ? 'white' : 'black'
            }}
          >
            {index + 1}
          </div>
          <span>{t(`nacional-project.modal.${certificate}`)}</span>
        </li>
      ))}
    </ul>
  );
};

const getModalTextContent = (
  t: TFunction,
  currentCertificateName: string,
  concludedIndex: number,
  challengeMeta: ChallengeMeta,
  registrationQuiz: number,
  close: () => void
): JSX.Element => (
  <div className='left-side'>
    <div className='intro-texts'>
      <span className='new-stage-unlocked'>
        {t('nacional-project.modal.new-stage-unlocked')}
      </span>

      <h1 className='main-title'>
        {t('nacional-project.modal.concluded-certificate', {
          currentCertificateFinish: currentCertificateName
        })}
      </h1>

      <span className='subtitle'>
        {t('nacional-project.modal.keep-going')}
        <br />
      </span>
    </div>

    <ModalSteps
      t={t}
      concludedIndex={concludedIndex}
      registrationQuiz={registrationQuiz}
    />

    <div className='bottom-area'>
      <Button
        buttonType={ButtonTypes.Primary}
        width='440px'
        onClick={() => {
          close();
          void navigate(challengeMeta.nextChallengePath ?? '');
        }}
      >
        {t('nacional-project.modal.next-cert')}
      </Button>
    </div>
  </div>
);

const getLastCertificateModal = (
  t: TFunction,
  currentCertificateName: string,
  concludedIndex: number,
  registrationQuiz: number,
  close: () => void
): JSX.Element => (
  <div className='left-side'>
    <div className='intro-texts'>
      <span className='journey-completed'>
        {t('nacional-project.modal.journey-completed')}
      </span>

      <h1 className='main-title'>
        {t('nacional-project.modal.congrats-last-trail')}
      </h1>

      <div className='subtitle'>
        {t('nacional-project.modal.last-trail-subtitle', {
          currentCertificateFinish: currentCertificateName
        })}
        <br />
      </div>
    </div>

    <ModalSteps
      t={t}
      concludedIndex={concludedIndex}
      registrationQuiz={registrationQuiz}
    />

    <div className='bottom-area'>
      <Button buttonType={ButtonTypes.Primary} width='440px' onClick={close}>
        {t('buttons.close')}
      </Button>
    </div>
  </div>
);

interface ModalContentProps {
  t: TFunction;
  currentCertificateName: string;
  lastCertificateIndex: number;
  challengeMeta: ChallengeMeta;
  registrationQuiz: number;
  close: () => void;
}

const getModalContent = ({
  t,
  currentCertificateName,
  lastCertificateIndex,
  challengeMeta,
  registrationQuiz,
  close
}: ModalContentProps): JSX.Element => {
  const currentLastCertificateIndex = 3;
  if (lastCertificateIndex === currentLastCertificateIndex) {
    return getLastCertificateModal(
      t,
      currentCertificateName,
      lastCertificateIndex,
      registrationQuiz,
      close
    );
  }

  return getModalTextContent(
    t,
    currentCertificateName,
    lastCertificateIndex,
    challengeMeta,
    registrationQuiz,
    close
  );
};

const normalizaName = (challengeName: string) => {
  return challengeName?.split(' - ')[1];
};

const ModalCtaRegistration: React.FC<ModalCtaRegistrationProps> = ({
  isOpen,
  user,
  challengeMeta,
  close
}) => {
  const { t } = useTranslation();

  if (
    ![AccessLevel.COMPLETE_NACIONAL, AccessLevel.LIMITED_NACIONAL].includes(
      user.access
    )
  ) {
    return null;
  }

  const nationalTrails = user.nationalTrail ?? [];

  const currentCheckpoint = getCheckpointById(challengeMeta.id) as {
    certificateName?: string;
  } | null;

  const currentCheckpointName: string =
    currentCheckpoint?.certificateName ?? '';

  const currentCertificateName = normalizaName(currentCheckpointName);

  const currentCheckpointIndex: number = getCheckpointIndexById(
    challengeMeta.id
  );

  const registrationQuiz = user.registrationQuiz;

  const lastExtraCertificationIndex = nationalTrails.length - 1;

  const lastCertificateIndex =
    lastExtraCertificationIndex > currentCheckpointIndex
      ? lastExtraCertificationIndex
      : currentCheckpointIndex;

  return (
    <Modal
      animation={false}
      dialogClassName='modal-cta-registration'
      keyboard
      onHide={close}
      show={isOpen}
    >
      <Modal.Body>
        <div className='close-icon'>
          <Close3 onClick={close} />
        </div>

        <div className='modal-body-content'>
          {getModalContent({
            t,
            currentCertificateName,
            lastCertificateIndex,
            challengeMeta,
            registrationQuiz,
            close
          })}

          <div className='right-side'>
            <img src={medal} alt='medal' width={354} height={453} />
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

ModalCtaRegistration.displayName = 'ModalCtaRegistration';

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ModalCtaRegistration);
